import React from 'react';
import { useTheme, Box, Typography  } from '@mui/material';
import { ResponsivePie } from '@nivo/pie';


const PieChart = ({ 
  data = [], 
  isTotalVisible = true,
  title = "",
  digit = 1,
  titleMarginTop = 6,
  topOffSet = 0,
  bottomOffSet = 0, 
  itemsSpacingOffSet = 0,
  itemWidthOffSet = 0, 
  translateXOffSet = 0,
  translateYOffSet = 0,
}) => {
  const theme = useTheme();

  if (!data || data.length === 0 || !Array.isArray(data)) {
    return (
      <Box p={1} sx={{ height: '100%', width: '100%' }}>        
        <Typography>Nothing to display</Typography>
      </Box>
    );
  }

  const total = Number(data.reduce((acc, cur) => acc + cur.value, 0)?.toFixed(digit));
  const formattedData = data.map(e => ({
    ...e,
    value: Number(e.value?.toFixed(digit)), 
    percentage: total && Number(((e.value / total) * 100)?.toFixed(digit)) 
  }));

  const MARGIN_X = 50;
  
  return (    
    <Box 
      display="flex" 
      flexDirection="column" 
      gap={1} 
      
      sx={{ height: '100%', width: '100%' }}
    >
      {title && 
        <Typography 
          variant="h6" 
          align="center"   
          mt={titleMarginTop}       
        >
          {title}
        </Typography>
      }
      
      <Box sx={{ position: 'relative', height: '100%', width: '100%' }}>
        <ResponsivePie
          data={formattedData}        
          margin={{ 
            top: 25 + (topOffSet || 0),
            right: MARGIN_X, 
            bottom: 65 + (bottomOffSet || 0), 
            left: MARGIN_X, 
          }}
          sortByValue={true}
          innerRadius={0.4}
          padAngle={0.7}
          cornerRadius={6}
          colors={{ scheme: 'set2' }}
          activeOuterRadiusOffset={8}
          borderWidth={1}
          borderColor={{
            from: 'color',
            modifiers: [['darker', 0.2]],
          }}
          enableArcLinkLabels={true}
          enableArcLabels={true}
          arcLabel={(e) => `${e.value.toLocaleString()} ~ ${e.data.percentage}%`}
          arcLinkLabelsDiagonalLength={10}
          arcLinkLabelsStraightLength={12}
          arcLinkLabelsTextColor={theme.palette.text.primary}
          arcLinkLabelsThickness={2}
          arcLinkLabelsColor={{ from: 'color' }}
          arcLabelsSkipAngle={5}
          arcLabelsTextColor={{
            from: 'color',
            modifiers: [['brighter', 3]],
          }}        
          legends={[
            {
              anchor: 'bottom',
              direction: 'row',
              justify: false,
              translateX: 15 + translateXOffSet,
              translateY: 45 + translateYOffSet,
              itemsSpacing: itemsSpacingOffSet,
              itemWidth: 100 + itemWidthOffSet,
              itemHeight: 18,
              itemTextColor: theme.palette.text.primary,
              itemDirection: 'left-to-right',
              itemOpacity: 1,
              symbolSize: 12,
              symbolShape: 'circle',
              effects: [
                {
                  on: 'hover',
                  style: {
                    itemTextColor: theme.palette.text.primary,
                  },
                },
              ],              
            },
          ]}
          layers={[
            'arcs',
            'arcLabels',
            'arcLinkLabels',
            'legends',
            ...(isTotalVisible
              ? [
                  ({ centerX, centerY, innerRadius }) => {
                    const fontSize = Math.max(
                      10,
                      Math.min(innerRadius * 0.25, 16)
                    );
                    
                    return (
                      <text
                        x={centerX}
                        y={centerY}
                        textAnchor="middle"
                        dominantBaseline="central"
                        style={{
                          fontSize,
                          fontWeight: 600,
                          fill: theme.palette.text.primary,
                          pointerEvents: 'none',
                        }}
                      >
                        {total?.toLocaleString()}
                      </text>
                    );
                  },
                ]
              : []),
          ]}
          theme={{ 
            labels: {
              text: {
                fontSize: '12px'
              }
            },    
            tooltip: {            
              container: {
                background: theme.palette.neutral.light, 
                color: theme.palette.text.primary, 
                borderRadius: '4px', 
                padding: '8px', 
              },
            },                    
          }}
        />
      </Box>   
    </Box>
  );
};

export default PieChart;
